/* eslint-disable complexity */
import React, { ReactNode, useCallback, useState } from 'react';

import { useCrudActivityContext } from '@futrli/shared-services';
import { UILoader } from '@futrli/ui-components';
import { Forecast } from '@futrli-sdk/predict';

import { BasicInfo } from '../../../components/basic-info/basic-info';
import { DeletePredictionDialog } from '../../../components/delete-prediction/delete-prediction';
import { FormulaReferenceOption } from '../../../../../utils/formulas/types';
import { ImpactPreview } from '../impact-preview/impact-preview';
import { PaymentTreatment } from '../../../types';
import { PredictionForm } from '../../../components/prediction-form/prediction-form';

import { useStandardOrganisationPredictionBuilderContext } from '../../../context/standard-organisation-prediction-builder-context';
import { usePredictionsInfoContext } from '../../../../detail/context/predictions-info-context';
import { usePredictManagementContext } from '../../../../../predict-management/context/predict-management-context';

import { PredictionFormContainer } from '../../../components/prediction-form-container/prediction-form-container';

interface PredictionEditorProps {
  children?: ReactNode;
  footerContent?: ReactNode;
  formError?: boolean;
  onSave?: () => void;
  onSaveAndNew?: () => void;
  show?: boolean;
  onClose?: () => void;
  isPredictionTypeActive?: boolean;
  formulaEditorOptions?: FormulaReferenceOption[];
}

export const PredictionEditor = ({
  children,
  footerContent,
  formError = false,
  onSave,
  onSaveAndNew,
  show = true,
  formulaEditorOptions,
  onClose,
  isPredictionTypeActive
}: PredictionEditorProps) => {
  const {
    accountsData,
    accountIndex,
    handleAccountIndexChange,
    handleDeletePrediction,
    handlePredictionNameChange,
    handleVatPercentageChange,
    handleAddNewMultiPaymentTreatment,
    handleChangeMultiPaymentTreatmentData,
    handleNonCashAccountIndexChange,
    handlePaymentTermsChange,
    handlePaymentTreatmentChange,
    isEditing,
    isSaveDisabled,
    multiPaymentData,
    nonCashAccountIndex,
    paymentTerms,
    paymentTreatment,
    predictionContentTouched,
    predictionName,
    vatPercentage
  } = useStandardOrganisationPredictionBuilderContext();

  const { addDeleting, isBusy } = useCrudActivityContext();
  const { forecast, isConsolidation } = usePredictionsInfoContext();
  const { currency } = usePredictManagementContext();

  const [isOpenDeleteDialog, setIsOpenDeleteDialog] = useState(false);

  const handleDeleteConfirm = useCallback(async () => {
    setIsOpenDeleteDialog(false);
    addDeleting('prediction', handleDeletePrediction());
  }, [addDeleting, handleDeletePrediction]);

  const handleDeleteClick = useCallback(() => setIsOpenDeleteDialog(true), []);

  const handleCloseDeleteDialog = useCallback(() => setIsOpenDeleteDialog(false), []);

  if (isBusy) {
    if (isEditing || isPredictionTypeActive) {
      return <UILoader />;
    }
    return null;
  }

  if (!accountsData || !show) {
    return null;
  }

  return (
    <PredictionFormContainer
      footerContent={footerContent}
      onClose={onClose}
      onSave={onSave}
      onSaveAndNew={onSaveAndNew}
      onDelete={handleDeleteClick}
      isEditing={isEditing}
      saveDisabled={isSaveDisabled}
    >
      <div>
        <BasicInfo
          predictionName={predictionName}
          accountIndex={accountIndex}
          handlePredictionNameChange={handlePredictionNameChange}
          handleVatPercentageChange={handleVatPercentageChange}
          accountRows={accountsData}
          vatPercentage={vatPercentage}
          currency={currency}
          defaultVatRate={isConsolidation ? undefined : (forecast as Forecast)?.vat_rate}
          vatDisabled={
            isConsolidation
              ? undefined
              : (forecast as Forecast)?.vat_disabled || paymentTreatment === PaymentTreatment.NON_CASH_TRANSFER
          }
          predictionContentTouched={predictionContentTouched}
          handleAccountIndexAutocompleteChange={handleAccountIndexChange}
        />
        <PredictionForm
          accountsData={accountsData}
          formError={formError}
          onMultiPaymentDataAddNew={handleAddNewMultiPaymentTreatment}
          onMultiPaymentDataChange={handleChangeMultiPaymentTreatmentData}
          onNonCashAccountIndexChange={handleNonCashAccountIndexChange}
          onPaymentTermsChange={handlePaymentTermsChange}
          onPaymentTreatmentChange={handlePaymentTreatmentChange}
          multiPaymentData={multiPaymentData}
          nonCashAccountIndex={nonCashAccountIndex}
          paymentTerms={paymentTerms}
          paymentTreatment={paymentTreatment}
          predictionContentTouched={predictionContentTouched}
        >
          {children}
        </PredictionForm>
        <ImpactPreview formulaEditorOptions={formulaEditorOptions} />
        <DeletePredictionDialog
          isOpenDeleteDialog={isOpenDeleteDialog}
          handleToggleDeleteDialog={handleCloseDeleteDialog}
          onSubmit={handleDeleteConfirm}
        />
      </div>
    </PredictionFormContainer>
  );
};
